import { useForm } from "@mantine/form"
import { useDebouncedValue } from "@mantine/hooks"
import {
  CheckCircle,
  Circle,
  ClockClockwise,
  MagnifyingGlass,
  Plus,
  Warning,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react"
import { ShieldCheck } from "@phosphor-icons/react/dist/ssr"
import {
  Avatar,
  Button,
  Divider,
  InputGroup,
  OverflowMenu,
  Tag,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { AgreementRenewAlertModal } from "~/components/Agreement/AgreementRenewAlertModal"
import { TableSummary } from "~/components/TableSummary"
import FilterTypes from "~/constants/FilterTypes"
import { AppRoutes } from "~/constants/routes"
import { useAllStorageLocationsQuery } from "~/generated/graphql"
import {
  StorageFilter,
  StorageLocationFilters,
} from "~/pages/d/storage-locations/StorageLocationFilters"
import { config } from "~/pages/pavti/config"

const StorageLocations = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const page = Number(params.get("page")) || 1

  const [search, setSearch] = useState(params.get("search") || "")
  const [debouncedSearch] = useDebouncedValue(search.trim(), 500)
  const form = useForm<StorageFilter>()

  const [filter, setFilter] = useState<StorageFilter>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.storageFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.storageFilter, JSON.stringify(filter))
  }, [filter])

  const [{ data, fetching }] = useAllStorageLocationsQuery({
    variables: {
      search: debouncedSearch,
      limit: config.pageSize,
      offset: (page - 1) * config.pageSize,
      filters: {
        agreement_start_date_from: filter?.agreement_start_date_from
          ? dayjs(filter?.agreement_start_date_from).format("YYYY-MM-DD")
          : undefined,
        agreement_start_date_to: filter?.agreement_start_date_to
          ? dayjs(filter?.agreement_start_date_to).format("YYYY-MM-DD")
          : undefined,
        agreement_end_date_from: filter.agreement_end_date_from
          ? dayjs(filter.agreement_end_date_from).format("YYYY-MM-DD")
          : undefined,
        agreement_end_date_to: filter.agreement_end_date_to
          ? dayjs(filter.agreement_end_date_to).format("YYYY-MM-DD")
          : undefined,
        storage_location_created_at_from: filter?.created_at_from
          ? dayjs(filter?.created_at_from).format("YYYY-MM-DD")
          : undefined,
        storage_location_created_at_to: filter?.created_at_to
          ? dayjs(filter?.created_at_to).format("YYYY-MM-DD")
          : undefined,
        agreement_is_active: filter?.is_active?.id,
        agreement_is_verified: filter?.is_verified?.id,
        agreement_lease_tenure_kind: filter?.tenure_kind?.id,
        agreement_ownership_kind: filter?.ownership_kind?.id,

        sub_location_kind: filter?.sub_location_kind?.id,
      },
    },
    requestPolicy: "cache-and-network",
  })

  const totalLocations =
    data?.all_storage_locations?.total_storage_locations || 0

  const summaryCards = [
    {
      id: "total",
      title: "Total Storage Location",
      value: String(totalLocations || "--"),
      intentClass: "text-green-300",
      iconBgClass: "bg-[#F6FAE3]",
      icon: CheckCircle,
    },
    {
      id: "low",
      title: "Approval Pending",
      value: String(
        data?.all_storage_locations?.pending_storage_locations || "--"
      ),
      intentClass: "text-amber-500",
      iconBgClass: "bg-amber-100",
      icon: Warning,
    },
    {
      id: "out",
      title: "Approved Agreements",
      value: String(
        data?.all_storage_locations?.approved_storage_locations || "--"
      ),
      intentClass: "text-red-500",
      iconBgClass: "bg-red-100",
      icon: ShieldCheck,
    },
    {
      id: "expiring_soon",
      title: "Expiring Soon",
      value: String(
        data?.all_storage_locations
          ?.expiring_soon_agreement_storage_locations || "--"
      ),
      intentClass: "text-[#F54900]",
      iconBgClass: "bg-[#FFF7ED]",
      icon: ClockClockwise,
    },
    {
      id: "expired",
      title: "Expired Agreements",
      value: String(
        data?.all_storage_locations?.expired_agreement_storage_locations || "--"
      ),
      intentClass: "text-red-500",
      iconBgClass: "bg-red-100",
      icon: WarningCircle,
    },
  ]

  const COLUMNS = [
    {
      Header: "Storage Location",
      accessor: "storage_location.name",
      Cell({ value, row: { original } }: any) {
        if (!value) return "--"
        return (
          <Cell
            title={
              <div
                className="cursor-pointer"
                onClick={() => {
                  navigate(
                    `/d/storage-locations/${original?.storage_location?.id}`
                  )
                }}
              >
                {value}
              </div>
            }
            // subtitle={
            //   <Tag variant="outline" intent="info" className="!rounded-full">
            //     {original?.storage_location?.codename}
            //   </Tag>
            // }
          />
        )
      },
    },
    {
      Header: "Storage Sub-Location",
      accessor: "storage_location.sub_locations",
      Cell({ value }: any) {
        if (!value?.length) return "--"
        return (
          <Cell
            title={
              <OverflowMenu
                items={value}
                visibleItems={1}
                getItemProps={item => {
                  return {
                    className: "!border-none !text-gray-800 underline",
                  }
                }}
              />
            }
          />
        )
      },
    },
    {
      Header: "Storage Type",
      accessor: "storage_location.sub_locations",
      id: "storage_type",
      Cell({ value }: any) {
        if (!value?.length) return "--"
        return (
          <Cell
            title={
              <OverflowMenu
                items={value}
                visibleItems={1}
                getTitle={item => {
                  return item?.kind?.name
                }}
                getItemProps={item => {
                  return {
                    className: "!border-none !text-gray-800",
                  }
                }}
              />
            }
          />
        )
      },
    },
    {
      Header: "Address",
      accessor: "storage_location.address.village",
      Cell({ value, row: { original } }: any) {
        if (!value) return "--"
        const raw_address = original?.storage_location?.address?.raw_address
        return (
          <Cell
            titleClassName="cursor-pointer"
            title={
              <Tooltip content={raw_address}>
                <div>{value}</div>
              </Tooltip>
            }
          />
        )
      },
    },
    {
      Header: "POC",
      accessor: "storage_location.pocs",
      Cell({ value }: any) {
        if (!value?.length) return "--"
        return <Cell title={<OverflowMenu items={value} visibleItems={1} />} />
      },
    },
    {
      Header: "Quantity",
      accessor: "trade_used_quantity_metric_ton",
      Cell({ value, row: { original } }: any) {
        return (
          <div className="flex flex-col">
            <span className="text-sm font-600 text-orange-600">
              {value?.toLocaleString() || 0} MT/{" "}
              {original.total_capacity_quantity_metric_ton?.toLocaleString() ||
                0}{" "}
              MT
            </span>
          </div>
        )
      },
    },
    {
      Header: "Agreement",
      accessor: "current_agreement.start_date",
      Cell({ value, row: { original } }: any) {
        if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("DD MMM, YYYY") + " to"}
            subtitle={dayjs(original.current_agreement?.end_date).format(
              "DD MMM, YYYY"
            )}
            subtitleClassName="font-700 !text-gray-800"
          />
        )
      },
    },
    {
      Header: "Rent Amount",
      accessor: "current_agreement.lease_amount",
      Cell({ value, row: { original } }: any) {
        if (!value) return "--"
        return (
          <Cell
            title={inr(value)}
            titleClassName="!text-green font-600"
            subtitle={
              original?.current_agreement?.lease_tenure_kind ? (
                <Tag variant="outline" intent="info" className="!rounded-full">
                  {original?.current_agreement?.lease_tenure_kind}
                </Tag>
              ) : (
                "--"
              )
            }
          />
        )
      },
    },
    {
      Header: "Alerts",
      accessor: "alerts",
      Cell({ value, row: { original } }: any) {
        if (!value?.length) return "--"
        const getIcon = (icon: any) => {
          if (icon === 1)
            return (
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle
                  size={20}
                  weight="fill"
                  className="text-green-300"
                />
              </div>
            )
          if (icon === 2)
            return (
              <div className="bg-yellow-100 p-2 rounded-lg">
                <Warning size={20} weight="fill" className="text-yellow-500" />
              </div>
            )
          if (icon === 3)
            return (
              <div className="bg-red-100 p-2 rounded-lg">
                <XCircle size={20} weight="fill" className="text-red-500" />
              </div>
            )
          if (icon === 4)
            return (
              <div className="bg-yellow-100 p-2 rounded-lg">
                <ClockClockwise
                  size={20}
                  weight="fill"
                  className="text-yellow-500"
                />
              </div>
            )
          return null
        }
        const alert = value[0]

        return value?.length === 1 ? (
          <div className="flex gap-2 items-center">
            <div className="">{getIcon(alert.icon)}</div>
            <div className="flex flex-col">
              <span
                className={`text-xs font-600 ${
                  alert.icon === 1
                    ? "text-green-400"
                    : alert.icon === 2
                    ? "text-[#A16207]"
                    : alert.icon === 3
                    ? "text-red-600"
                    : alert.icon === 4
                    ? "text-yellow-500"
                    : "text-gray-600"
                }`}
              >
                {alert.title}
              </span>
              <span className="text-[10px] text-gray-500">{alert.message}</span>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            {value?.map((alert: any) => {
              return (
                <Tooltip
                  placement="bottom"
                  content={
                    <div className="flex flex-col">
                      <span>{alert.title}</span>
                      <span className="text-[10px]">{alert.message}</span>
                    </div>
                  }
                >
                  <div>{getIcon(alert.icon)}</div>
                </Tooltip>
              )
            })}
          </div>
        )
      },
    },
    {
      Header: "Placed By",
      accessor: "current_agreement.created_by",
      Cell({ value, row: { original } }: any) {
        if (!original?.current_agreement) return "--"
        return (
          <Tooltip
            content={
              <div>
                <p>{value?.name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(original?.current_agreement?.created_at).format(
                    "DD MMM 'YY | h:mm a"
                  )}
                </p>
              </div>
            }
          >
            <Avatar className="relative text-center" name={value?.name} />
          </Tooltip>
        )
      },
    },
    {
      Header: "Status",
      accessor: "agreement_status",
      Cell({ value, row: { original } }: any) {
        if (!value) return "--"
        const pending = value === "Agreement Approval Pending"
        const approved = value === "Valid"
        const expired = value === "Agreement Expired"
        const expiring = value === "Agreement Expiring Soon"
        const rejected = value === "Rejected"
        const invalid = value === "Invalid"
        const waitingToBeActive = value === "Agreement Waiting to be Active"

        return (
          <div className="flex items-center gap-1 ">
            <Circle
              weight="fill"
              className={
                pending || waitingToBeActive
                  ? "text-blue"
                  : approved
                  ? "text-green-500"
                  : expired || rejected || invalid
                  ? "text-red-500"
                  : expiring
                  ? "text-yellow-500"
                  : "text-gray-500"
              }
            />
            <span
              className={classNames(
                "text-xs font-600 whitespace-nowrap",
                pending || waitingToBeActive
                  ? "text-blue"
                  : approved
                  ? "text-green-500"
                  : expired || rejected || invalid
                  ? "text-red-500"
                  : expiring
                  ? "text-yellow-500"
                  : "text-gray-500"
              )}
            >
              {pending
                ? "Storage Approval Pending"
                : approved
                ? "Storage Approved"
                : expired
                ? "Storage Expired"
                : expiring
                ? "Storage Expiring Soon"
                : rejected
                ? "Storage Rejected"
                : invalid
                ? "Invalid"
                : waitingToBeActive
                ? "Storage Waiting to be Active"
                : "Unknown"}
            </span>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 pt-4 px-4 border-b-1 border-b-gray-300">
        <div></div>
        <div className="flex items-center gap-4">
          <InputGroup
            className="!w-full"
            leftElement={<MagnifyingGlass />}
            inputProps={{
              value: search,
              placeholder: "Search by Location Name",
              onChange(e) {
                const value = e.target.value
                setSearch(value)
                if (value) {
                  params.set("search", value)
                } else {
                  params.delete("search")
                }
                setParams(params)
              },
            }}
          />
          <StorageLocationFilters filter={filter} setFilter={setFilter} />
        </div>
        <div className="flex justify-end items-center gap-3">
          <Button
            leftIcon={<Plus className="mr-2" />}
            onClick={() => {
              navigate(AppRoutes.createStorageLocation)
            }}
          >
            Storage
          </Button>
        </div>
      </header>
      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-5 gap-4">
          <TableSummary summary={summaryCards} loading={fetching} />
        </div>
      </section>
      <section className="mx-4 mb-2 bg-white rounded-b-xl border border-t-0 border-gray-200">
        <Divider />
        <TableView
          isLoading={fetching}
          columns={COLUMNS as any}
          data={data?.all_storage_locations?.storage_locations || []}
          paginationProps={{
            total: Math.ceil(totalLocations / config.pageSize),
            page: page,
            onChange(page) {
              params.set("page", page.toString())
              params.set("search", "")
              setParams(params)
            },
          }}
          relative={false}
          className="!mt-0 overflow-x-auto"
          tableClassName="!mt-0 !min-h-[600px] max-h-[600px] overflow-y-scroll"
          meta={
            <span className="text-xs text-gray-500">
              {totalLocations} results
            </span>
          }
        />
        {/* </div> */}
      </section>
      <AgreementRenewAlertModal />
    </>
  )
}

export default StorageLocations
